// Requirement questions, written for a broker sharing a client's needs. The
// client page reads the same list through `forClient`, which speaks to the
// visitor directly.

const has = (s, id, v) => Array.isArray(s[id]) && s[id].includes(v);

export const KINDS = { home: 'Flat or house', land: 'Land' };

// Tapping a range is faster than typing on a phone, and gives amounts the
// app can match on. Exact amounts can still be typed under the ranges.
export const BUDGET_RANGES = [
  ['Under 50 L', '', '50 L'],
  ['50 L – 1 Cr', '50 L', '1 Cr'],
  ['1 – 1.5 Cr', '1 Cr', '1.5 Cr'],
  ['1.5 – 2.5 Cr', '1.5 Cr', '2.5 Cr'],
  ['2.5 – 5 Cr', '2.5 Cr', '5 Cr'],
  ['Above 5 Cr', '5 Cr', ''],
];

// Same spelling every time, so property matching finds them.
export const PUNE_AREAS = [
  'Baner', 'Balewadi', 'Aundh', 'Pashan', 'Bavdhan', 'Kothrud', 'Wakad', 'Hinjewadi',
  'Pimple Saudagar', 'Kharadi', 'Viman Nagar', 'Wagholi', 'Hadapsar', 'Magarpatta',
  'Koregaon Park', 'Kalyani Nagar', 'Undri', 'NIBM', 'Kondhwa', 'Sus',
];

export const ABOUT = {
  client: [
    { id: 'name', q: 'Your name', type: 'text', ph: 'Full name', required: true },
    { id: 'phone', q: 'Your WhatsApp number', hint: 'We reply here within 2 hours.', type: 'tel', ph: '98765 43210', required: true },
  ],
  broker: [
    { id: 'broker', q: 'Your name', type: 'text', ph: 'Full name', required: true },
    { id: 'brokerPhone', q: 'Your WhatsApp number', type: 'tel', ph: '98765 43210', required: true },
    { id: 'name', q: 'Client name or a short label', type: 'text', ph: 'e.g. Mr. Rao, IT couple', required: true },
  ],
};

export const HOME = [
  { title: 'The property', items: [
    { id: 'purpose', q: 'What is the purchase for?', type: 'single', options: ['Own stay', 'Investment: rent', 'Investment: resale'] },
    { id: 'ptype', q: 'What type of property?', type: 'multi', options: ['Apartment', 'Row house / Villa', 'Plot', 'Other'] },
    { id: 'status', q: 'Under construction or ready to move?', type: 'single', options: ['Under construction', 'Ready to move', 'Open to both'] },
    { id: 'possession', q: 'Possession needed by', type: 'text', ph: 'e.g. Dec 2027',
      when: s => s.status === 'Under construction' || s.status === 'Open to both' },
    { id: 'oc', q: 'Must the OC (occupancy certificate) be received?', type: 'single', options: ['Yes', 'No', 'Not sure'],
      when: s => s.status === 'Ready to move' || s.status === 'Open to both' },
    { id: 'building', q: 'Single standing building or a society?', hint: 'Single standing means one building, no big society around it.', type: 'single', options: ['Single standing building', 'Society (many buildings)', 'Any'] },
    { id: 'age', q: 'How old can the building be?', type: 'single', options: ['Brand new', 'Up to 5 years', 'Up to 10 years', 'Up to 15 years', 'Does not matter'] },
    { id: 'bhk', q: 'How many bedrooms (BHK)?', type: 'multi', options: ['1', '2', '3', '4', '5+'] },
    { id: 'size', q: 'What is the smallest size that works?', type: 'text', ph: 'e.g. 1200 sq ft carpet' },
    { id: 'floor', q: 'Any floor preference?', type: 'multi', options: ['Low', 'Middle', 'High', 'Any'] },
  ]},
  { title: 'Place and price', items: [
    { id: 'areas', q: 'Which areas?', hint: 'Tap all that work, or type others.', type: 'places', ph: 'Other areas' },
    { id: 'near', q: 'Near which place?', hint: 'Office, school or family home.', type: 'text', ph: 'e.g. Hinjewadi Phase 1' },
    { id: 'radius', q: 'How far from that place is okay?', type: 'single', options: ['2 km', '5 km', '10 km', '15 km or more'] },
    { id: 'budget', q: 'What is the budget?', type: 'budget', ranges: BUDGET_RANGES, fields: [{ id: 'budgetMin', label: 'From', ph: '1.5 Cr' }, { id: 'budgetMax', label: 'Up to', ph: '2.5 Cr' }] },
    { id: 'basis', q: 'Does the budget include registration, GST and parking?', type: 'single', options: ['Yes, all included', 'No, price only', 'Not sure'] },
    { id: 'parking', q: 'How much parking is needed?', type: 'single', options: ['1 car', '2 cars', 'Bike only', 'Not needed'] },
  ]},
  { title: 'Must-haves', items: [
    { id: 'must', q: 'Any non-negotiables?', hint: 'Things the client will not give up.', type: 'area', ph: 'e.g. East facing, society with a clubhouse' },
    { id: 'deal', q: 'Any deal breakers?', hint: 'Anything that is an instant no.', type: 'area', ph: 'e.g. main road, ground floor' },
    { id: 'nice', q: 'Anything that would be nice to have?', hint: 'Amenities, view, facing, vastu.', type: 'area' },
  ]},
  { title: 'Timing and money', items: [
    { id: 'closing', q: 'When does the client want to buy?', type: 'single', options: ['Within 1 month', '1 to 3 months', '3 to 6 months', '6 to 12 months', 'Just exploring'] },
    { id: 'funding', q: 'How will the client pay?', type: 'single', options: ['Home loan', 'Own money', 'Both'] },
    { id: 'loan', q: 'What is the loan status?', type: 'single', options: ['Approved', 'Applied', 'Not yet applied'],
      when: s => s.funding === 'Home loan' || s.funding === 'Both' },
    { id: 'decision', q: 'Who makes the final decision?', type: 'single', options: ['Client alone', 'Family also decides'] },
    { id: 'visit', q: 'When can the client visit properties?', type: 'single', options: ['This weekend', 'Next 2 weeks', 'Later'] },
  ]},
  { title: 'A few more', items: [
    { id: 'family', q: 'Who will live there?', type: 'text', ph: 'Adults, kids, elders, pets' },
    { id: 'seen', q: 'Already seen or rejected any property?', hint: 'Which one, and why.', type: 'area' },
    { id: 'other', q: 'Anything else I should know?', type: 'area' },
  ]},
];

export const LAND = [
  { title: 'The land', items: [
    { id: 'l_purpose', q: 'Investment or own use?', type: 'single', options: ['Investment', 'Own use (non-investment)', 'Not sure yet'] },
    { id: 'l_invtype', q: 'What type of investment?', type: 'single',
      options: ['Hold and sell later', 'Rent or lease it out', 'Develop and sell plots', 'Farm income', 'Commercial project'],
      when: s => s.l_purpose === 'Investment' },
    { id: 'l_usewhat', q: 'What will the client do on the land?', type: 'single',
      options: ['Factory or industry', 'Warehouse', 'Resort or hotel', 'School or hospital', 'Farmhouse or home', 'Farming', 'Other'],
      when: s => s.l_purpose === 'Own use (non-investment)' },
    { id: 'l_usenote', q: 'Tell me about the business or use', type: 'text', ph: 'e.g. packaging unit, needs 200 kW power',
      when: s => s.l_purpose === 'Own use (non-investment)' },
    { id: 'l_type', q: 'Type of land', hint: 'Pick all that fit. Green belt means green zone land where building is limited.', type: 'multi',
      options: ['Residential plot', 'Commercial plot', 'Industrial land', 'Agricultural land', 'Green belt', 'Hill or lake side', 'Any'] },
    { id: 'l_industrial', q: 'Industrial plot: MIDC or private?', type: 'single', options: ['MIDC plot', 'Private industrial land', 'Any'],
      when: s => has(s, 'l_type', 'Industrial land') },
    { id: 'l_farm', q: 'What does the farm need?', type: 'text', ph: 'e.g. black soil, fruit trees, water source',
      when: s => has(s, 'l_type', 'Agricultural land') },
    { id: 'l_na', q: 'Should the land be NA?', hint: 'NA means the land is officially approved for non-farm use, like building.', type: 'single', options: ['Yes, already NA', 'NA can be done later', 'Not needed'] },
    { id: 'l_unit', q: 'Which unit does the client use?', hint: '1 acre = 40 gunthas.', type: 'single', options: ['Acres', 'Gunthas', 'Sq ft'] },
    { id: 'l_size', q: 'How much land?', type: 'pair', fields: [{ id: 'l_sizeMin', label: 'At least', ph: 'e.g. 2' }, { id: 'l_sizeMax', label: 'Up to', ph: 'e.g. 5' }] },
  ]},
  { title: 'Place and price', items: [
    { id: 'l_areas', q: 'Which areas, villages or talukas?', type: 'text', ph: 'e.g. Mulshi, Maval, Khed' },
    { id: 'l_near', q: 'Close to which place?', hint: 'Highway, city, airport, ring road or factory belt.', type: 'text', ph: 'e.g. Pune-Mumbai expressway' },
    { id: 'l_radius', q: 'How far from that place is okay?', type: 'single', options: ['2 km', '5 km', '10 km', '25 km or more'] },
    { id: 'l_budget', q: 'What is the budget?', type: 'pair', fields: [{ id: 'l_budgetMin', label: 'From', ph: '2 Cr' }, { id: 'l_budgetMax', label: 'Up to', ph: '5 Cr' }] },
    { id: 'l_basis', q: 'Is the budget total or per unit?', type: 'single', options: ['Total amount', 'Per acre', 'Per guntha', 'Per sq ft'] },
  ]},
  { title: 'Land details', items: [
    { id: 'l_road', q: 'What road access is needed?', type: 'single', options: ['Must touch a main road', 'Any road is fine', 'Road not needed'] },
    { id: 'l_terrain', q: 'What should the land be like?', type: 'multi', options: ['Flat', 'Sloping', 'Hill view', 'Lake or river nearby'] },
    { id: 'l_water', q: 'What water and power are needed?', type: 'multi', options: ['Borewell or well', 'River or canal water', 'Electricity nearby', 'Not needed'] },
    { id: 'l_papers', q: 'Which papers must be clean?', hint: 'Tick what must be in order before the client buys.', type: 'multi', options: ['Clear title', '7/12 extract', 'No court case', 'NA order', 'Approved layout', 'Not sure'] },
    { id: 'l_owners', q: 'Who is selling?', hint: 'Land with many owners usually takes longer to close.', type: 'single', options: ['One owner', 'Many owners are okay', 'Builder or developer', 'Any'] },
  ]},
  { title: 'Must-haves', items: [
    { id: 'l_must', q: 'Any non-negotiables?', hint: 'Things the client will not give up.', type: 'area', ph: 'e.g. road touching, clear title, near highway' },
    { id: 'l_deal', q: 'Any deal breakers?', hint: 'Anything that is an instant no.', type: 'area', ph: 'e.g. court case, no road, many owners' },
  ]},
  { title: 'Timing and money', items: [
    { id: 'l_closing', q: 'When does the client want to buy?', type: 'single', options: ['Within 1 month', '1 to 3 months', '3 to 6 months', '6 to 12 months', 'Just exploring'] },
    { id: 'l_funding', q: 'How will the client pay?', type: 'single', options: ['Own money', 'Loan', 'Both'] },
    { id: 'l_loan', q: 'What is the loan status?', type: 'single', options: ['Approved', 'Applied', 'Not yet applied'],
      when: s => s.l_funding === 'Loan' || s.l_funding === 'Both' },
    { id: 'l_decision', q: 'Who makes the final decision?', type: 'single', options: ['Client alone', 'Partners or family also decide'] },
    { id: 'l_visit', q: 'When can the client visit the land?', type: 'single', options: ['This weekend', 'Next 2 weeks', 'Later'] },
  ]},
  { title: 'A few more', items: [
    { id: 'l_seen', q: 'Already seen or rejected any land?', hint: 'Which one, and why.', type: 'area' },
    { id: 'l_other', q: 'Anything else I should know?', type: 'area' },
  ]},
];

// The client page talks to the visitor, not about "the client".
const YOU = [
  [/^What is the client looking for\?$/, 'What are you looking for?'],
  [/^Client alone$/, 'Just me'],
  [/^Partners or family also decide$/, 'Partners or family also decide'],
  [/will the client/g, 'will you'],
  [/does the client/g, 'do you'],
  [/can the client/g, 'can you'],
  [/the client buys/g, 'you buy'],
  [/the client will/g, 'you will'],
  [/Anything else I should know\?/, 'Anything else we should know?'],
  [/Tell me about/, 'Tell us about'],
];
const speak = (text) => (text ? YOU.reduce((t, [from, to]) => t.replace(from, to), text) : text);

export function forClient(sections) {
  return sections.map(sec => ({
    ...sec,
    items: sec.items.map(it => ({
      ...it,
      q: speak(it.q),
      hint: speak(it.hint),
      options: it.options?.map(speak),
    })),
  }));
}

// The order screens appear in. One question per screen; a list of ids is
// one screen holding fields that belong together (name and number). Both
// forms ask the questions that decide the search, then offer the rest.
const CORE = {
  home: ['kind', 'purpose', 'ptype', 'bhk', 'status', 'possession', 'oc', 'areas', 'budget', 'closing', 'funding', 'loan', 'visit'],
  land: ['kind', 'l_purpose', 'l_invtype', 'l_usewhat', 'l_usenote', 'l_type', 'l_industrial', 'l_farm', 'l_unit', 'l_size', 'l_areas', 'l_budget', 'l_closing', 'l_funding', 'l_loan', 'l_visit'],
};
const DETAIL = {
  home: ['size', 'floor', 'building', 'age', 'near', 'radius', 'basis', 'parking', 'must', 'deal', 'nice', 'family', 'decision', 'seen', 'other'],
  land: ['l_basis', 'l_na', 'l_near', 'l_radius', 'l_road', 'l_terrain', 'l_water', 'l_papers', 'l_owners', 'l_must', 'l_deal', 'l_decision', 'l_seen', 'l_other'],
};

export function flowFor(form, kind, withDetail) {
  const k = kind === 'land' ? 'land' : 'home';
  const detail = withDetail ? DETAIL[k] : [];
  if (form === 'broker') return [['broker', 'brokerPhone', 'name'], ...CORE[k], 'gate', ...detail, 'send'];
  return [...CORE[k], 'gate', ...detail, ['name', 'phone']];
}

export const DETAIL_IDS = new Set([...DETAIL.home, ...DETAIL.land]);

const KIND_ITEM = { id: 'kind', q: 'What is the client looking for?', type: 'kind' };

export function itemsFor(form) {
  const all = [...ABOUT[form], KIND_ITEM, ...HOME.flatMap(s => s.items), ...LAND.flatMap(s => s.items)];
  const items = form === 'client' ? forClient([{ items: all }])[0].items : all;
  return Object.fromEntries(items.map(it => [it.id, it]));
}
